import * as React from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Divider,
    FormLabel,
    FormControl,
    Link as MuiLink,
    TextField,
    Typography,
    Card as MuiCard,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { School as SchoolIcon } from '@mui/icons-material';
import videmyLogo from '../../assets/videmy-logo.png';
import { useAuth } from '../../context/AuthContext';
import SocialLoginButtons from './SocialLoginButtons';

const SignInContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
        maxWidth: '480px', // Mobile/Tablet
    },
    [theme.breakpoints.up('md')]: {
        maxWidth: '100%', // Desktop: Fill the Grid Item
        paddingLeft: theme.spacing(8),
        paddingRight: theme.spacing(8),
    },
}));

export default function SignInCard() {
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    const { login } = useAuth();
    const navigate = useNavigate();

    const validateInputs = () => {
        const email = document.getElementById('email');
        const password = document.getElementById('password');

        let isValid = true;

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
            setEmailError(true);
            setEmailErrorMessage('Please enter a valid email address.');
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        if (!password.value) {
            setPasswordError(true);
            setPasswordErrorMessage('Password is required.');
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        return isValid;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (emailError || passwordError) {
            return;
        }
        if (!validateInputs()) {
            return;
        }

        const data = new FormData(event.currentTarget);
        const email = data.get('email');
        const password = data.get('password');

        setLoading(true);
        const result = await login(email, password);

        if (result.success) {
            toast.success('Welcome back!');
            navigate('/dashboard');
        } else {
            console.error('Login Error:', result);
            if (result.type === 'user_blocked') {
                toast.error('Your account has been deactivated. Please contact the Administrator.');
            } else if (result.type === 'user_invalid_credentials' || result.type === 'general_argument_invalid') {
                // Inline error only, no Toast
                setPasswordError(true);
                setPasswordErrorMessage('Invalid credentials (or user not found)');
            } else if (result.type === 'general_rate_limit_exceeded') {
                // Rate limit (429)
                toast.error('Too many login attempts. Please try again later.');
            } else {
                // Other System/Network Errors
                toast.error(result.error || 'Login failed.');
            }
        }
        setLoading(false);
    };

    return (
        <SignInContainer>


            <Typography
                component="h1"
                variant="h4"
                sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
            >
                Sign in
            </Typography>

            <Box
                component="form"
                onSubmit={handleSubmit}
                noValidate
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    gap: 2,
                }}
            >
                <FormControl>
                    <FormLabel htmlFor="email">Email</FormLabel>
                    <TextField
                        error={emailError}
                        helperText={emailErrorMessage}
                        id="email"
                        type="email"
                        name="email"
                        placeholder="your@email.com"
                        autoComplete="email"
                        autoFocus
                        required
                        fullWidth
                        variant="outlined"
                        color={emailError ? 'error' : 'primary'}
                    />
                </FormControl>
                <FormControl>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <FormLabel htmlFor="password">Password</FormLabel>
                        <MuiLink
                            component={Link}
                            to="/forgot-password"
                            variant="body2"
                            onClick={(e) => {
                                e.preventDefault();
                                toast.info('Feature coming soon!');
                            }}
                        >
                            Forgot your password?
                        </MuiLink>
                    </Box>
                    <TextField
                        error={passwordError}
                        helperText={passwordErrorMessage}
                        name="password"
                        placeholder="••••••"
                        type="password"
                        id="password"
                        autoComplete="current-password"
                        autoFocus={false}
                        required
                        fullWidth
                        variant="outlined"
                        color={passwordError ? 'error' : 'primary'}
                    />
                </FormControl>
                <FormControlLabel
                    control={<Checkbox value="remember" color="primary" />}
                    label="Remember me"
                />
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? 'Signing in...' : 'Sign in'}
                </Button>
                <Typography sx={{ textAlign: 'center' }}>
                    Don&apos;t have an account?{' '}
                    <MuiLink
                        component={Link}
                        to="/register"
                        variant="body2"
                        sx={{ alignSelf: 'center' }}
                    >
                        Sign up
                    </MuiLink>
                </Typography>
            </Box>
            <Divider>or</Divider>
            <SocialLoginButtons />
        </SignInContainer>
    );
}
